/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import { EditorState } from 'draft-js';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
  useUnmount,
  useMemo,
  useCallback,
} from '@web-stories-wp/react';

/**
 * Internal dependencies
 */
import { getExplicitEmptyLineHeight } from './emptyLineHeight';
import useRichText from './useRichText';
import customInlineDisplay from './customInlineDisplay';
import adjustListDepth from './adjustListDepth';
import ScreenSpaceTextEditor from './screenSpaceTextEditor';
import handleListShortcut from './handleListShortcut';
import { insertListSoftNewline } from './util';
import { FONT_SIZE, LINE_HEIGHT } from './customConstants';
import { LIST_BLOCK_TYPES } from './constants';
import { styleToFontSize } from './formatters/fontSize';
import { styleToLineHeight } from './formatters/lineHeight';

export const MATERIALIZED_FONT_SIZE_BLOCK_CLASS =
  'st-materialized-font-size-block';
const LINE_BREAK_FONT_SIZE_PREFIX = 'ST-LINEBREAK-FONT-SIZE-';
const LINE_BREAK_LINE_HEIGHT_PREFIX = 'ST-LINEBREAK-LINE-HEIGHT-';
const EMPTY_BLOCK_LINE_HEIGHT_CLASS_PREFIX = 'st-empty-line-height-';
const DEFAULT_CARET_COLOR = '#000000';
const identity = (value) => value;

const ACTIVE_EMPTY_TYPOGRAPHY_PROPERTIES = {
  fontFamily: '--st-active-empty-font-family',
  fontSize: '--st-active-empty-font-size',
  fontStyle: '--st-active-empty-font-style',
  fontWeight: '--st-active-empty-font-weight',
  letterSpacing: '--st-active-empty-letter-spacing',
  lineHeight: '--st-active-empty-line-height',
  textDecoration: '--st-active-empty-text-decoration',
  textTransform: '--st-active-empty-text-transform',
};

// Draft skips rendering its contents for native, selection-only updates. Target
// the active block from this wrapper so typing metrics do not depend on stale
// blockStyleFn classes inside Draft's cached contents.
const EditorLayout = styled.div`
  ${({ $closingParagraphCss }) => $closingParagraphCss}
  & [data-block][data-offset-key=${({ $activeEmptyBlockKey }) =>
    JSON.stringify(`${$activeEmptyBlockKey}-0-0`)}] {
    font-family: var(--st-active-empty-font-family, inherit);
    font-size: var(--st-active-empty-font-size, inherit);
    font-style: var(--st-active-empty-font-style, inherit);
    font-weight: var(--st-active-empty-font-weight, inherit);
    letter-spacing: var(--st-active-empty-letter-spacing, inherit);
    line-height: var(--st-active-empty-line-height, inherit);
    text-decoration: var(--st-active-empty-text-decoration, inherit);
    text-transform: var(--st-active-empty-text-transform, inherit);
  }
`;

export function getMaterializedFontSizeBlockClass(block) {
  return block.getLength() > 0 ? MATERIALIZED_FONT_SIZE_BLOCK_CLASS : '';
}

export function getActiveEmptyBlockKey(editorState) {
  if (!editorState) {
    return null;
  }
  const selection = editorState.getSelection();
  if (!selection.isCollapsed()) {
    return null;
  }
  const blockKey = selection.getStartKey();
  const block = editorState.getCurrentContent().getBlockForKey(blockKey);
  return block?.getLength() === 0 ? blockKey : null;
}

export function getActiveEmptyTypographyStyle(
  editorState,
  inlineStyleContext,
  inheritedLineHeight
) {
  if (!getActiveEmptyBlockKey(editorState)) {
    return undefined;
  }
  const inlineCss = customInlineDisplay(
    editorState.getCurrentInlineStyle(),
    inlineStyleContext
  );
  const style = Object.entries(ACTIVE_EMPTY_TYPOGRAPHY_PROPERTIES).reduce(
    (result, [property, customProperty]) => {
      if (inlineCss[property] !== undefined) {
        result[customProperty] = inlineCss[property];
      }
      return result;
    },
    {}
  );
  const lineHeightProperty = ACTIVE_EMPTY_TYPOGRAPHY_PROPERTIES.lineHeight;
  if (
    style[lineHeightProperty] === undefined &&
    Number.isFinite(inheritedLineHeight)
  ) {
    style[lineHeightProperty] =
      inlineStyleContext.lineHeightToCss?.(inheritedLineHeight) ??
      inheritedLineHeight;
  }
  return Object.keys(style).length ? style : undefined;
}

export function getRichTextBlockClass(
  block,
  emptyBlockLineHeightClass = '',
  hasList = false
) {
  // List rows reserve the box/marker line height in both HTML and SVG. Only
  // paragraphs should shed that minimum when their inline text gets smaller.
  if (hasList && block.getType() !== 'unstyled') {
    return '';
  }
  return [getMaterializedFontSizeBlockClass(block), emptyBlockLineHeightClass]
    .filter(Boolean)
    .join(' ');
}

export function getRichTextInlineStyle(
  styles,
  inlineStyleContext,
  defaultFontSize,
  materializeFontSizes,
  defaultLineHeight,
  materializeLineHeights = false
) {
  const lineHeightToCss = inlineStyleContext.lineHeightToCss || identity;
  const lineBreakFontSizeStyle = styles.find((style) =>
    style.startsWith(LINE_BREAK_FONT_SIZE_PREFIX)
  );
  const lineBreakFontSize = lineBreakFontSizeStyle
    ? Number(lineBreakFontSizeStyle.slice(LINE_BREAK_FONT_SIZE_PREFIX.length))
    : null;
  const lineBreakLineHeightStyle = styles.find((style) =>
    style.startsWith(LINE_BREAK_LINE_HEIGHT_PREFIX)
  );
  const lineBreakLineHeight = lineBreakLineHeightStyle
    ? Number(
        lineBreakLineHeightStyle.slice(LINE_BREAK_LINE_HEIGHT_PREFIX.length)
      )
    : null;
  return {
    ...(materializeFontSizes
      ? { fontSize: inlineStyleContext.fontSizeToCss(defaultFontSize) }
      : null),
    ...(materializeLineHeights
      ? { lineHeight: lineHeightToCss(defaultLineHeight) }
      : null),
    ...customInlineDisplay(styles, inlineStyleContext),
    ...(Number.isFinite(lineBreakFontSize)
      ? { fontSize: inlineStyleContext.fontSizeToCss(lineBreakFontSize) }
      : null),
    ...(Number.isFinite(lineBreakLineHeight)
      ? { lineHeight: lineHeightToCss(lineBreakLineHeight) }
      : null),
  };
}

/**
 * Resolve the edit-only caret color without collapsing an absent inline color
 * into Draft's default black. Pending typing styles take precedence over the
 * current run; element-level SVG fills can explicitly prefer their fallback.
 */
export function getCaretColor({
  editorState,
  inlineStyleContext = {},
  fallbackColor = DEFAULT_CARET_COLOR,
  preferFallbackColor = false,
}) {
  if (preferFallbackColor && fallbackColor) {
    return fallbackColor;
  }

  if (!editorState) {
    return fallbackColor || DEFAULT_CARET_COLOR;
  }

  const inlineStyles = editorState.getCurrentInlineStyle();
  const inlineColor = customInlineDisplay(
    inlineStyles,
    inlineStyleContext
  )?.color;

  return inlineColor || fallbackColor || DEFAULT_CARET_COLOR;
}

function getCharacterLineHeight(styles, defaultLineHeight) {
  const lineHeightStyle = styles.find((style) =>
    style.startsWith(`${LINE_HEIGHT}-`)
  );
  return lineHeightStyle
    ? styleToLineHeight(lineHeightStyle) ?? defaultLineHeight
    : defaultLineHeight;
}

function getCharacterFontSize(styles, defaultFontSize) {
  const fontSizeStyle = styles.find((style) =>
    style.startsWith(`${FONT_SIZE}-`)
  );
  return fontSizeStyle
    ? styleToFontSize(fontSizeStyle) ?? defaultFontSize
    : defaultFontSize;
}

// Draft adds a synthetic newline after a terminal soft break so its end remains
// editable. HTML/SVG use that break to close the paragraph instead. Reclaim only
// the synthetic line's space before the next block; keep the content and offsets.
export function getClosingParagraphCss(
  content,
  defaultFontSize,
  defaultLineHeight,
  fontSizeToCss,
  lineHeightToCss = identity
) {
  if (
    !content
      ?.getBlockMap()
      .some((block) => LIST_BLOCK_TYPES.includes(block.getType()))
  ) {
    return '';
  }
  const lastKey = content.getLastBlock().getKey();
  return content
    .getBlocksAsArray()
    .filter(
      (block) =>
        block.getType() === 'unstyled' &&
        block.getKey() !== lastKey &&
        block.getText().endsWith('\n')
    )
    .map((block) => {
      const styles = block.getCharacterList().last().getStyle();
      const size = getCharacterFontSize(styles, defaultFontSize);
      const lineHeight = lineHeightToCss(
        getCharacterLineHeight(styles, defaultLineHeight)
      );
      return `& [data-block][data-offset-key=${JSON.stringify(
        `${block.getKey()}-0-0`
      )}] {
      margin-bottom: calc(-1 * ${fontSizeToCss(size)} * ${lineHeight});
    }`;
    })
    .join('\n');
}

/**
 * Draft represents Enter-created blank lines as empty blocks without styled
 * characters. Preserve only the preceding effective line height for their
 * layout; other inline typography remains the block default.
 */
export function getEmptyBlockLineHeights(editorState, defaultLineHeight) {
  const lineHeights = new Map();
  if (!editorState || !Number.isFinite(defaultLineHeight)) {
    return lineHeights;
  }

  let previousLineHeight = defaultLineHeight;
  editorState
    .getCurrentContent()
    .getBlockMap()
    .forEach((block) => {
      if (block.getLength() === 0) {
        previousLineHeight =
          getExplicitEmptyLineHeight(block) ?? previousLineHeight;
        lineHeights.set(block.getKey(), previousLineHeight);
        return;
      }

      const text = block.getText();
      block.getCharacterList().forEach((character, index) => {
        if (text[index] !== '\n') {
          previousLineHeight = getCharacterLineHeight(
            character.getStyle(),
            defaultLineHeight
          );
        }
      });
    });

  return lineHeights;
}

function getEmptyBlockLineHeightClass(lineHeight) {
  if (!Number.isFinite(lineHeight)) {
    return '';
  }
  const encodedLineHeight = String(lineHeight)
    .replace(/-/g, 'm')
    .replace(/\+/g, 'p')
    .replace(/\./g, 'd');
  return `${EMPTY_BLOCK_LINE_HEIGHT_CLASS_PREFIX}${encodedLineHeight}`;
}

export function getEmptyBlockLineHeightCss(
  lineHeights,
  lineHeightToCss = identity
) {
  return [...new Set(lineHeights.values())]
    .filter(Number.isFinite)
    .map(
      (lineHeight) =>
        `.${getEmptyBlockLineHeightClass(
          lineHeight
        )}{line-height:${lineHeightToCss(lineHeight)};}`
    )
    .join('');
}

// Keep an unchanged private style in its original OrderedSet position. Removing
// and appending size/height in separate passes otherwise reorders them forever.
function setLineBreakStyle(styles, prefix, value) {
  const matching = styles.filter((style) => style.startsWith(prefix));
  const nextStyle = value === null ? null : `${prefix}${value}`;
  if (
    nextStyle
      ? matching.size === 1 && matching.has(nextStyle)
      : matching.size === 0
  ) {
    return styles;
  }
  const next = styles.subtract(matching);
  return nextStyle ? next.add(nextStyle) : next;
}

/**
 * Soft newlines are real Draft characters. If they keep the box default size,
 * an invisible character can become the tallest run after a visible word is
 * resized. Give each newline the size of the character immediately before it
 * for layout only; the private style is ignored by HTML serialization.
 */
export function materializeLineBreakFontSizes(editorState, defaultFontSize) {
  if (!editorState || !Number.isFinite(defaultFontSize)) {
    return editorState;
  }

  const content = editorState.getCurrentContent();
  let contentChanged = false;
  const blockMap = content.getBlockMap().map((block) => {
    const text = block.getText();
    let previousFontSize = defaultFontSize;
    let blockChanged = false;
    const characterList = block.getCharacterList().map((character, index) => {
      const styles = character.getStyle();
      const nextStyles = setLineBreakStyle(
        styles,
        LINE_BREAK_FONT_SIZE_PREFIX,
        text[index] === '\n' ? previousFontSize : null
      );
      previousFontSize =
        text[index] === '\n'
          ? defaultFontSize
          : getCharacterFontSize(styles, defaultFontSize);

      if (nextStyles.equals(styles)) {
        return character;
      }
      blockChanged = true;
      return character.set('style', nextStyles);
    });

    if (!blockChanged) {
      return block;
    }
    contentChanged = true;
    return block.set('characterList', characterList);
  });

  if (!contentChanged) {
    return editorState;
  }
  return EditorState.set(editorState, {
    currentContent: content.set('blockMap', blockMap),
  });
}

/** Give soft newlines the preceding effective line height for layout only. */
export function materializeLineBreakLineHeights(
  editorState,
  defaultLineHeight
) {
  if (!editorState || !Number.isFinite(defaultLineHeight)) {
    return editorState;
  }

  const content = editorState.getCurrentContent();
  let contentChanged = false;
  const blockMap = content.getBlockMap().map((block) => {
    const text = block.getText();
    let previousLineHeight = defaultLineHeight;
    let blockChanged = false;
    const characterList = block.getCharacterList().map((character, index) => {
      const styles = character.getStyle();
      const nextStyles = setLineBreakStyle(
        styles,
        LINE_BREAK_LINE_HEIGHT_PREFIX,
        text[index] === '\n' ? previousLineHeight : null
      );
      if (text[index] !== '\n') {
        previousLineHeight = getCharacterLineHeight(styles, defaultLineHeight);
      }

      if (nextStyles.equals(styles)) {
        return character;
      }
      blockChanged = true;
      return character.set('style', nextStyles);
    });

    if (!blockChanged) {
      return block;
    }
    contentChanged = true;
    return block.set('characterList', characterList);
  });

  if (!contentChanged) {
    return editorState;
  }
  return EditorState.set(editorState, {
    currentContent: content.set('blockMap', blockMap),
  });
}

function RichTextEditor(
  {
    sessionKey,
    content,
    fontReferences,
    defaultFont,
    defaultFontSize,
    defaultLineHeight,
    defaultFontWeight,
    defaultFontStyle,
    fontSizeToCss,
    lineHeightToCss = identity,
    materializeFontSizes,
    materializeLineHeights,
    fallbackCaretColor,
    preferFallbackCaretColor,
    screenSpaceScale,
    onChange,
  },
  ref
) {
  const editorRef = useRef(null);
  const {
    state: { editorState, formatterContext },
    actions: {
      setStateFromValue,
      updateEditorState,
      getHandleKeyCommand,
      getValueFromContent,
      handlePastedText,
      handleClipboardCopy,
      handleClipboardCut,
      handleClipboardPaste,
      clearState,
    },
  } = useRichText();

  // Load state from parent when content changes
  useEffect(() => {
    setStateFromValue({
      sessionKey,
      content,
      fontReferences,
      defaultFont,
      defaultFontSize,
      defaultLineHeight,
      defaultFontWeight,
      defaultFontStyle,
    });
  }, [
    setStateFromValue,
    sessionKey,
    content,
    fontReferences,
    defaultFont,
    defaultFontSize,
    defaultLineHeight,
    defaultFontWeight,
    defaultFontStyle,
  ]);

  const currentContent = editorState?.getCurrentContent();
  const hasList = useMemo(
    () =>
      currentContent
        ?.getBlockMap()
        .some((block) => LIST_BLOCK_TYPES.includes(block.getType())) || false,
    [currentContent]
  );
  const closingParagraphCss = useMemo(
    () =>
      getClosingParagraphCss(
        currentContent,
        defaultFontSize,
        defaultLineHeight,
        fontSizeToCss,
        lineHeightToCss
      ),
    [
      currentContent,
      defaultFontSize,
      defaultLineHeight,
      fontSizeToCss,
      lineHeightToCss,
    ]
  );
  const newValue = useMemo(
    () => getValueFromContent(currentContent),
    [currentContent, getValueFromContent]
  );
  const lastEmittedValue = useRef(null);

  // Push updates to parent when state changes
  useEffect(() => {
    if (newValue === null) {
      return;
    }

    const serializedValue = JSON.stringify(newValue);
    if (serializedValue === lastEmittedValue.current) {
      return;
    }
    lastEmittedValue.current = serializedValue;
    onChange(newValue);
  }, [newValue, onChange]);

  const inlineStyleContext = useMemo(
    () => ({ ...formatterContext, fontSizeToCss, lineHeightToCss }),
    [formatterContext, fontSizeToCss, lineHeightToCss]
  );
  const caretColor = useMemo(
    () =>
      getCaretColor({
        editorState,
        inlineStyleContext,
        fallbackColor: fallbackCaretColor,
        preferFallbackColor: preferFallbackCaretColor,
      }),
    [
      editorState,
      fallbackCaretColor,
      inlineStyleContext,
      preferFallbackCaretColor,
    ]
  );
  const customStyleFn = useCallback(
    (styles, block) => {
      const inlineStyle = getRichTextInlineStyle(
        styles,
        inlineStyleContext,
        defaultFontSize,
        materializeFontSizes || hasList,
        defaultLineHeight,
        materializeLineHeights
      );
      // Empty blocks own their spacing. Draft's BR leaf must not impose the
      // box-default line height, which offsets the caret below compact blanks.
      return materializeLineHeights && block.getLength() === 0
        ? { ...inlineStyle, lineHeight: 0 }
        : inlineStyle;
    },
    [
      defaultFontSize,
      defaultLineHeight,
      inlineStyleContext,
      hasList,
      materializeFontSizes,
      materializeLineHeights,
    ]
  );
  const layoutEditorState = useMemo(() => {
    let nextEditorState = editorState;
    if (materializeFontSizes) {
      nextEditorState = materializeLineBreakFontSizes(
        nextEditorState,
        defaultFontSize
      );
    }
    if (materializeLineHeights) {
      nextEditorState = materializeLineBreakLineHeights(
        nextEditorState,
        defaultLineHeight
      );
    }
    return nextEditorState;
  }, [
    defaultFontSize,
    defaultLineHeight,
    editorState,
    materializeFontSizes,
    materializeLineHeights,
  ]);
  const materializeTypography = materializeFontSizes || materializeLineHeights;
  const emptyBlockLineHeights = useMemo(
    () =>
      materializeLineHeights
        ? getEmptyBlockLineHeights(editorState, defaultLineHeight)
        : new Map(),
    [defaultLineHeight, editorState, materializeLineHeights]
  );
  const emptyBlockLineHeightCss = useMemo(
    () => getEmptyBlockLineHeightCss(emptyBlockLineHeights, lineHeightToCss),
    [emptyBlockLineHeights, lineHeightToCss]
  );
  const activeEmptyBlockKey = useMemo(
    () => (materializeTypography ? getActiveEmptyBlockKey(editorState) : null),
    [editorState, materializeTypography]
  );
  const activeEmptyTypographyStyle = useMemo(() => {
    if (!materializeTypography) {
      return undefined;
    }
    return getActiveEmptyTypographyStyle(
      editorState,
      inlineStyleContext,
      materializeLineHeights
        ? emptyBlockLineHeights.get(activeEmptyBlockKey)
        : undefined
    );
  }, [
    activeEmptyBlockKey,
    editorState,
    emptyBlockLineHeights,
    inlineStyleContext,
    materializeLineHeights,
    materializeTypography,
  ]);
  const hasEditorState = Boolean(editorState);
  const blockStyleFn = useCallback(
    (block) =>
      materializeTypography || hasList
        ? getRichTextBlockClass(
            block,
            materializeLineHeights
              ? getEmptyBlockLineHeightClass(
                  emptyBlockLineHeights.get(block.getKey())
                )
              : '',
            hasList
          )
        : '',
    [
      emptyBlockLineHeights,
      hasList,
      materializeLineHeights,
      materializeTypography,
    ]
  );
  const editorContainerStyle = useMemo(
    () => ({
      ...(activeEmptyTypographyStyle || null),
      caretColor: `var(--st-caret-preview-color, ${caretColor})`,
    }),
    [activeEmptyTypographyStyle, caretColor]
  );

  const handleTab = useCallback(
    (event) => {
      const nextState = adjustListDepth(editorState, event.shiftKey ? -1 : 1);
      if (!nextState) {
        return;
      }

      event.preventDefault();
      if (nextState !== editorState) {
        updateEditorState(nextState);
      }
    },
    [editorState, updateEditorState]
  );

  const handleBeforeInput = useCallback(
    (chars, currentEditorState) => {
      const nextState = handleListShortcut(currentEditorState, chars);
      if (!nextState) {
        return 'not-handled';
      }

      updateEditorState(nextState);
      return 'handled';
    },
    [updateEditorState]
  );

  const handleReturn = useCallback(
    (event, currentEditorState) => {
      if (!event.shiftKey) {
        return 'not-handled';
      }

      const nextState = insertListSoftNewline(currentEditorState);
      if (!nextState) {
        return 'not-handled';
      }

      updateEditorState(nextState);
      return 'handled';
    },
    [updateEditorState]
  );

  // On unmount, clear state in provider
  useUnmount(clearState);

  // Allow parent to focus editor and access main node
  useImperativeHandle(
    ref,
    () => ({
      focus: () => editorRef.current?.focus?.(),
      getNode: () => editorRef.current?.editorContainer,
    }),
    []
  );

  if (!hasEditorState) {
    return null;
  }

  // Handle basic key commands such as bold, italic and underscore.
  const handleKeyCommand = getHandleKeyCommand();
  return (
    <EditorLayout
      $activeEmptyBlockKey={activeEmptyBlockKey}
      $closingParagraphCss={closingParagraphCss}
      style={editorContainerStyle}
    >
      {emptyBlockLineHeightCss ? (
        <style>{emptyBlockLineHeightCss}</style>
      ) : null}
      <ScreenSpaceTextEditor
        scale={screenSpaceScale}
        ref={editorRef}
        onChange={updateEditorState}
        editorState={layoutEditorState}
        handleBeforeInput={handleBeforeInput}
        handleKeyCommand={handleKeyCommand}
        handlePastedText={handlePastedText}
        onCopy={handleClipboardCopy}
        onCut={handleClipboardCut}
        onPaste={handleClipboardPaste}
        handleReturn={handleReturn}
        onTab={handleTab}
        customStyleFn={customStyleFn}
        blockStyleFn={blockStyleFn}
        spellCheck
        stripPastedStyles
      />
    </EditorLayout>
  );
}

const RichTextEditorWithRef = forwardRef(RichTextEditor);

RichTextEditor.propTypes = {
  sessionKey: PropTypes.string.isRequired,
  content: PropTypes.string.isRequired,
  fontReferences: PropTypes.object,
  defaultFont: PropTypes.object.isRequired,
  defaultFontSize: PropTypes.number.isRequired,
  defaultLineHeight: PropTypes.number.isRequired,
  defaultFontWeight: PropTypes.number,
  defaultFontStyle: PropTypes.string,
  fontSizeToCss: PropTypes.func.isRequired,
  lineHeightToCss: PropTypes.func,
  materializeFontSizes: PropTypes.bool,
  materializeLineHeights: PropTypes.bool,
  fallbackCaretColor: PropTypes.string,
  preferFallbackCaretColor: PropTypes.bool,
  screenSpaceScale: PropTypes.number,
  onChange: PropTypes.func.isRequired,
};

export default RichTextEditorWithRef;
